import { CompareArrows, KeyOffSharp, KeySharp } from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonProps,
  Card,
  CardContent,
  CardHeader,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectProps,
  SxProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { t } from 'i18next';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BlankCell } from '../../../common/components/Cells/BlankCell';
import { ComponentLoader } from '../../../common/components/ComponentLoader';
import { TraceType, getTraceTypeTracer } from '../../../common/enums/TraceType';
import { DateUtils } from '../../../common/utils/DateUtils';
import { prettyBytes } from '../../../common/utils/ui';
import { Report, ReportDone } from '../models/Report';
import { ReportDetailsSkeleton } from './skeletons/ReportDetailsSkeleton';

type RowProps = {
  label: string;
  value: ReactNode;
  sx?: SxProps;
};

const Row = ({ label, value, sx }: RowProps) => {
  return (
    <Typography data-role="report-details-row" variant="body1" component="div" sx={{ display: 'flex', mb: 1, ...(sx || {}) }}>
      <Typography sx={{ whiteSpace: 'nowrap', fontWeight: 700, pr: 0.75 }}>{label}:</Typography>
      {value}
    </Typography>
  );
};

type TraceProps = {
  traceName: string;
  traceType: TraceType | null;
  salt: string | null;
};

const TraceMeta = ({ traceName, traceType, salt }: TraceProps) => {
  if (!traceType) {
    return <>{traceName}</>;
  }
  if (salt) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={t('reportGeneratedWithProductionSafeTool', { tool: getTraceTypeTracer(traceType) })}>
          <KeySharp sx={{ p: 0.25, borderRadius: '50%', backgroundColor: 'grey.300' }} />
        </Tooltip>
        <Typography component="span" sx={{ pl: 0.5 }}>
          {traceName}
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title={t('reportGeneratedWithStandardTool', { tool: getTraceTypeTracer(traceType) })}>
        <KeyOffSharp sx={{ p: 0.25, borderRadius: '50%', backgroundColor: 'grey.300' }} />
      </Tooltip>
      <Typography component="span" sx={{ pl: 0.5 }}>
        {traceName}
      </Typography>
    </Box>
  );
};

type Props = {
  report: Report | null;
  loading: boolean;
  initialLoad: boolean;
  versionLink: (id: number) => string;
  compareLink: (report: Report) => string;
  profileLink: (report: Report) => string;
};

type VersionSelectOption = {
  formattedDate: string;
  formattedName: string;
};

export const ReportDetails = ({ report, loading, initialLoad, versionLink, compareLink, profileLink }: Props) => {
  const versionSelectRef = useRef<HTMLSelectElement>();
  const [versionOpen, setVersionOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!initialLoad && !loading && versionSelectRef.current) {
      versionSelectRef.current.focus();
    }
  }, [loading, initialLoad]);

  if (initialLoad) {
    return <ReportDetailsSkeleton />;
  }

  if (!report) return null;

  const buttonProps: ButtonProps = { variant: 'outlined', color: 'primary', sx: { py: 0.5, px: 0.75, minWidth: 0 } };
  const trace = report.getTrace();
  const profile = report.getProfile();
  const versions = report.getVersions().filter((v) => v.getRuleset() === report.getRuleset());
  const coverage = report instanceof ReportDone ? report.getCoverage() : null;
  const nonsensitiveSalt = report instanceof ReportDone ? report.getNonsensitiveSalt() : null;
  const createdAt = report.getCreatedAt();
  const completedAt = report instanceof ReportDone ? report.getCompletedAt() : null;
  const versionMap: Map<number, VersionSelectOption> = new Map(
    versions.map((v) => {
      const formattedDate = DateUtils.toUTCTimeString(v.getDate());
      return [v.getId(), { formattedDate, formattedName: `${formattedDate} (${v.getName()})` }];
    })
  );

  const labelA11yId = 'version-selector-label';
  const versionA11yProps: Partial<SelectProps<number>> = {
    labelId: labelA11yId,
    id: 'version-selector-input',
    SelectDisplayProps: versionOpen ? { 'aria-expanded': true, 'aria-controls': 'version-selector-menu' } : { 'aria-expanded': false, 'aria-controls': '' },
    MenuProps: { MenuListProps: { id: 'version-selector-menu' } },
    inputProps: { ref: versionSelectRef },
    onOpen: () => setVersionOpen(true),
    onClose: () => setVersionOpen(false),
    onChange: (e) => navigate(versionLink(Number(e.target.value))),
  };

  return (
    <ComponentLoader loading={loading}>
      <Card data-role="report-details">
        <CardHeader
          title={t('reportDetails')}
          titleTypographyProps={{ variant: 'h6' }}
          component={'h2'}
          sx={{ m: 0, pb: 0, '& .MuiCardHeader-action': { m: 0 } }}
          action={
            report instanceof ReportDone && report.canCompare() ? (
              <Tooltip title={t('compareReport')}>
                <Button {...buttonProps} href={compareLink(report)}>
                  <CompareArrows />
                </Button>
              </Tooltip>
            ) : (
              <></>
            )
          }
        />
        <CardContent>
          <Row label={t('name')} value={report.getName()} />
          <Typography
            data-role="report-details-row"
            variant="body1"
            component="div"
            sx={{ display: 'flex', mb: 1, alignItems: 'center', ...(trace === null ? { mb: 0 } : {}) }}
          >
            <InputLabel id={labelA11yId} sx={{ whiteSpace: 'nowrap', fontWeight: 700, pr: 0.75, color: 'inherit' }}>
              {t('version')}:
            </InputLabel>
            <Select
              variant="standard"
              value={report.getId()}
              sx={{ overflow: 'hidden', '& .MuiSelect-select': { py: 0.25 } }}
              renderValue={(versionId: number) => versionMap.get(versionId)?.formattedDate || ''}
              {...versionA11yProps}
            >
              {Array.from(versionMap, ([id, version]) => (
                <MenuItem key={id} value={id}>
                  {version.formattedName}
                </MenuItem>
              ))}
            </Select>
          </Typography>
          {completedAt !== null && <Row label={t('completedIn')} value={DateUtils.toDurationString(createdAt, completedAt)} />}
          {trace !== null && (
            <>
              <Row label={t('size')} value={prettyBytes(trace.getSize())} />
              <Row
                label={t('traceUsed')}
                sx={{ mb: 0 }}
                value={<TraceMeta traceName={trace.getName()} traceType={report.getTraceType() ?? null} salt={nonsensitiveSalt} />}
              />
            </>
          )}
          <Row
            label={t('profileUsed')}
            sx={{ my: 1 }}
            value={
              profile ? (
                <Link href={profileLink(report)}>{profile.getLabel()}</Link>
              ) : (
                <BlankCell blankText={t('noProfileInReport')} tooltip={t('noProfileInReportTooltip')} />
              )
            }
          />
          {coverage && (
            <Row
              label={t('coverage')}
              value={
                <Typography component="span">
                  {coverage.getPercent()}%
                  <Typography component="span" color="text.secondary" sx={{ pl: 0.5 }}>
                    (
                    {coverage.getDepth() !== null
                      ? t('reportCoverageWithDepth', { covered: coverage.getCovered(), total: coverage.getTotal(), depth: coverage.getDepth() })
                      : t('reportCoverage', { covered: coverage.getCovered(), total: coverage.getTotal() })}
                    )
                  </Typography>
                </Typography>
              }
            />
          )}
          {nonsensitiveSalt && <Row label={t('saltForKeyHash')} value={nonsensitiveSalt} />}
        </CardContent>
      </Card>
    </ComponentLoader>
  );
};
